import { useState, useEffect } from "react";
import { Loader } from "lucide-react";
import { metricsApi, userReportsApi } from "../../api";
import { Button, Modal, Select } from "../ui";
import type { UserReport } from "../../types/reports";
import type { MetricDefinition } from "../../types/metrics";

const CHART_TYPES = [
  { value: "number", label: "KPI / Single number", hint: "Shows the first numeric value prominently" },
  { value: "bar",    label: "Bar chart",            hint: "Compare values across categories" },
  { value: "line",   label: "Line chart",           hint: "Show trends over time" },
  { value: "table",  label: "Table",                hint: "Compact scrollable table" },
];

type PinModalProps = {
  report: UserReport;
  onClose: () => void;
  onSuccess: () => void;
};

export default function PinModal({ report, onClose, onSuccess }: PinModalProps) {
  const existing = report.dashboard_config;
  const [name, setName] = useState(report.name ?? "");
  const [chartType, setChartType] = useState(existing?.chart_type ?? "bar");
  const [xCol, setXCol] = useState(existing?.x_col ?? "");
  const [yCol, setYCol] = useState(existing?.y_col ?? "");
  const [cols, setCols] = useState<string[]>([]);
  const [numericCols, setNumericCols] = useState<string[]>([]);
  const [loadingCols, setLoadingCols] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
  const [metricId, setMetricId] = useState(report.metric_id ?? "");

  useEffect(() => {
    metricsApi.list().then((res) => setMetrics(res.data)).catch(() => {});
  }, []);

  // Sniff columns from a preview run
  useEffect(() => {
    userReportsApi.preview(report.id)
      .then((res) => {
        const rows = res.data?.rows ?? [];
        if (!rows.length) return;
        const allCols = Object.keys(rows[0]);
        setCols(allCols);
        const nums = allCols.filter((c) => typeof rows[0][c] === "number");
        setNumericCols(nums);
        if (!existing?.x_col) setXCol(allCols[0] ?? "");
        if (!existing?.y_col) setYCol(nums[0] ?? allCols[1] ?? "");
      })
      .catch(() => {})
      .finally(() => setLoadingCols(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sniff columns once per report
  }, [report.id]);

  async function handleSave() {
    setSaving(true);
    try {
      const trimmedName = name.trim();
      const updates: { name?: string; metric_id?: string | null } = {};
      if (trimmedName && trimmedName !== report.name) updates.name = trimmedName;
      if (metricId !== (report.metric_id ?? "")) updates.metric_id = metricId || null;
      if (Object.keys(updates).length > 0) {
        await userReportsApi.update(report.id, updates);
      }
      await userReportsApi.pin(report.id, {
        pinned: true,
        config: { chart_type: chartType, x_col: xCol, y_col: yCol },
      });
      onSuccess();
    } catch { return; } finally {
      setSaving(false);
    }
  }

  const needsAxes = chartType === "bar" || chartType === "line";

  return (
    <Modal
      onClose={onClose}
      size="sm"
      title="Edit widget"
      subtitle={report.name}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Widget name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
          />
        </div>
        {metrics.length > 0 && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Metric
            </label>
            <Select value={metricId} onChange={(e) => setMetricId(e.target.value)} className="w-full">
              <option value="">Custom SQL (this widget)</option>
              {metrics.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
            <p className="mt-1 text-[10px] text-gray-400">
              Link to a shared metric to use its definition — you'll be notified when it changes.
            </p>
          </div>
        )}
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Display as
          </label>
          <div className="grid grid-cols-2 gap-2">
            {CHART_TYPES.map((ct) => (
              <button
                key={ct.value}
                type="button"
                onClick={() => setChartType(ct.value)}
                className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  chartType === ct.value
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className="text-xs font-medium">{ct.label}</div>
                <div className="mt-0.5 text-[10px] text-gray-400">{ct.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {needsAxes && (
          <>
            {loadingCols ? (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader size={12} className="animate-spin" /> Detecting columns…
              </div>
            ) : cols.length === 0 ? (
              <p className="text-xs text-amber-600">Could not detect columns — you can still pin and edit later.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    {chartType === "line" ? "X axis (time / category)" : "Category (X axis)"}
                  </label>
                  <Select
                    size="sm"
                    value={xCol}
                    onChange={(e) => setXCol(e.target.value)}
                    className="w-full"
                  >
                    {cols.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Value (Y axis)
                  </label>
                  <Select
                    size="sm"
                    value={yCol}
                    onChange={(e) => setYCol(e.target.value)}
                    className="w-full"
                  >
                    {(numericCols.length > 0 ? numericCols : cols).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
